import { Button } from "../ui/button";
import { Trash2, FileText, ExternalLink } from "lucide-react";

interface MediaAsset {
  key: string;
  type: "image" | "raw";
  url: string;
  thumbnailUrl: string | null;
  size: number;
}

function formatSize(bytes: number) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

interface MediaPreviewModalProps {
  asset: MediaAsset;
  onClose: () => void;
  onDelete: (key: string) => void;
}

export function MediaPreviewModal({ asset, onClose, onDelete }: MediaPreviewModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="w-full max-w-3xl bg-card border border-border/80 rounded-2xl shadow-2xl overflow-hidden animate-zoom-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-border/60 flex justify-between items-center bg-muted/20">
          <div className="truncate pr-4">
            <h5 className="font-bold text-foreground truncate">{asset.key.split("-").pop()}</h5>
            <p className="text-[10px] text-muted-foreground mt-0.5 font-mono truncate">{asset.key}</p>
          </div>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground h-8 px-3"
          >
            Close
          </Button>
        </div>

        <div className="p-6 flex items-center justify-center bg-muted/10 min-h-[300px] max-h-[70vh] overflow-auto">
          {asset.type === "image" ? (
            <img
              src={asset.url}
              alt={asset.key}
              className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-sm"
            />
          ) : (
            <div className="flex flex-col items-center justify-center p-12 text-center space-y-4">
              <FileText className="h-20 w-20 text-brand-secondary/65" />
              <div>
                <p className="text-sm font-semibold text-foreground">Raw File Upload</p>
                <p className="text-xs text-muted-foreground mt-1">Size: {formatSize(asset.size)}</p>
              </div>
              <a
                href={asset.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary/95 rounded-xl transition-all"
              >
                Open in New Tab <ExternalLink className="h-4 w-4 ml-2" />
              </a>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-border/60 flex justify-between items-center bg-muted/20 text-xs">
          <span className="text-muted-foreground">Size: {formatSize(asset.size)}</span>
          <div className="flex gap-2">
            <a
              href={asset.url}
              download={asset.key.split("-").pop()}
              className="inline-flex items-center justify-center px-3 py-1.5 border border-border/60 hover:bg-muted/50 rounded-lg text-foreground font-semibold"
            >
              Download
            </a>
            <Button
              variant="destructive"
              onClick={() => onDelete(asset.key)}
              className="bg-destructive hover:bg-destructive/95 text-white h-8.5 rounded-lg"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete File
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MediaPreviewModal;
